import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { poll } from '@qover/utils/src/promise';
import { Heading2 } from '@qover/typography';
import { CircleLoader, PrimaryLink } from '@qover/molecules';
import { Modal } from './Modal';

const ModalContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${Heading2} {
    margin-bottom: 40px;
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.div`
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textQuote()};
  margin: 0 0 20px 0;
`;

type DocumentPreviewModalProps = {
  createDocumentPreview: () => Promise<void>;
  downloadUrl: string;
  onClose: () => void;
  pollDocumentPreview: () => Promise<any>;
};

export function DocumentPreviewModal(props: DocumentPreviewModalProps) {
  const [polling, setPolling] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    props
      .createDocumentPreview()
      .then(() => setPolling(true))
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    let pollFct: { promise: Promise<unknown>; clearTimeout: () => void };

    if (polling) {
      const retryConditionFn = (result: any) => result.code === 'NOT_AVAILABLE';
      pollFct = poll(props.pollDocumentPreview, retryConditionFn, 15000);

      pollFct.promise
        .then(() => {
          setShowDownloadButton(true);
          setPolling(false);
        })
        .catch(err => {
          console.error(err);
          setError(true);
          setPolling(false);
        });
    }

    return () => {
      if (pollFct?.clearTimeout) {
        pollFct.clearTimeout();
      }
    };
  }, [polling]);

  return (
    <Modal hideOverflow onClickOutside={props.onClose}>
      <Container>
        <CloseButton
          data-test="general.documentPreviewModalClose"
          onClick={props.onClose}
        >
          <Trans id="general.close" />
        </CloseButton>
      </Container>
      <ModalContainer>
        <Heading2 as="div">
          {polling ? (
            <Trans id="general.creatingYourDocument" />
          ) : (
            <Trans id="general.previewYourDocument" />
          )}
        </Heading2>
        {showDownloadButton && (
          <PrimaryLink
            href={props.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open document preview"
            onClick={props.onClose}
          >
            <Trans id="general.download" />
          </PrimaryLink>
        )}
        {error && <Trans id="general.documentPreviewModalError" />}
        {polling && <CircleLoader />}
      </ModalContainer>
    </Modal>
  );
}
